// IntegrityChecksPanel — Jun 2026.
//
// The "cross-check" engine, read-only. Each check compares two numbers that
// SHOULD agree and flags drift (green / amber / red). It never changes data —
// it only looks. Lives in the admin Overview, under the per-creator breakdown.
//
// Source: GET /api/admin/integrity-checks
import { useEffect, useState } from "react";
import { ShieldCheck, CaretDown, CaretRight, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DOT = { green: "#2F6B3A", amber: "#B8860B", red: "#C16E5A" };
const BG = { green: "#F0F7F1", amber: "#FBF6E9", red: "#FDF3F0" };
const LABEL = { green: "Healthy", amber: "Heads-up", red: "Needs attention" };

function Dot({ status }) {
  return <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DOT[status] || "#999" }} />;
}

export default function IntegrityChecksPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/integrity-checks");
      setData(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const [reconciling, setReconciling] = useState(false);
  const reconcileSubscribers = async () => {
    setReconciling(true);
    try {
      const r = await api.post("/admin/integrity-checks/reconcile-subscribers");
      const n = r.data?.reconciled || 0;
      toast.success(n ? `Reconciled ${n} silo counter${n === 1 ? "" : "s"}.` : "Counters already in sync.");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed.");
    } finally { setReconciling(false); }
  };
  const subscribersCheck = (data?.checks || []).find((c) => c.key === "subscribers");
  const showReconcile = subscribersCheck && subscribersCheck.status !== "green";

  return (
    <div className="bg-white border hairline rounded-xl p-5 shadow-sm" data-testid="integrity-checks-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} weight="duotone" className="text-[#4A5D4E]" />
        <div className="font-serif-display text-lg flex-1">Integrity checks</div>
        {data && (
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: BG[data.overall], color: DOT[data.overall] }}
            data-testid="integrity-overall">
            {data.green}/{data.total} healthy
          </span>
        )}
        <button onClick={reload} disabled={loading} data-testid="integrity-refresh"
          className="text-[#6B6A68] hover:text-[#1C1B1A] disabled:opacity-40">
          <ArrowsClockwise size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      <p className="text-xs text-[#6B6A68] mb-4">
        Read-only cross-checks. Each compares two numbers that should match and flags drift — it never changes anything.
      </p>

      {loading && !data && <div className="text-sm text-[#6B6A68]">Running checks…</div>}

      <div className="space-y-2">
        {(data?.checks || []).map((c) => {
          const isOpen = !!open[c.key];
          const hasDetail = (c.detail || []).length > 0;
          return (
            <div key={c.key} className="border hairline rounded-lg" data-testid={`integrity-check-${c.key}`}>
              <button
                onClick={() => hasDetail && setOpen((o) => ({ ...o, [c.key]: !o[c.key] }))}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                style={{ background: BG[c.status] }}
              >
                <Dot status={c.status} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1C1B1A] font-medium">{c.label}</div>
                  <div className="text-xs text-[#6B6A68]">{c.summary}</div>
                </div>
                <div className="text-right shrink-0 hidden sm:block">
                  <div className="text-xs text-[#1C1B1A] tabular-nums">{String(c.expected)} <span className="text-[#9A9892]">vs</span> {String(c.actual)}</div>
                  <div className="text-[10px] text-[#9A9892]">{c.expected_label} / {c.actual_label}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider shrink-0" style={{ color: DOT[c.status] }}>{LABEL[c.status]}</span>
                {hasDetail ? (isOpen ? <CaretDown size={14} className="text-[#6B6A68]" /> : <CaretRight size={14} className="text-[#6B6A68]" />) : <span className="w-3.5" />}
              </button>

              {isOpen && hasDetail && (
                <div className="px-3 py-2 border-t hairline bg-white" data-testid={`integrity-detail-${c.key}`}>
                  <table className="w-full text-xs">
                    <tbody>
                      {c.detail.map((d, i) => (
                        <tr key={i} className="border-b hairline last:border-0">
                          {Object.entries(d).map(([k, v]) => (
                            <td key={k} className="py-1.5 pr-3 text-[#3A3937]">
                              <span className="text-[10px] text-[#9A9892] uppercase tracking-wider mr-1">{k}</span>{String(v)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {isOpen && c.note && (
                <div className="px-3 pb-2 text-[11px] text-[#6B6A68] bg-white">{c.note}</div>
              )}
            </div>
          );
        })}
      </div>

      {showReconcile && (
        <div className="mt-3 flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-[#FBF6E9] border hairline" data-testid="integrity-reconcile-row">
          <div className="text-xs text-[#6B6A68]">
            A stored subscriber counter drifted from the real count. Reconcile resets the counters to match — no subscriptions or money are touched.
          </div>
          <button
            onClick={reconcileSubscribers}
            disabled={reconciling}
            data-testid="integrity-reconcile-subscribers"
            className="shrink-0 text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40"
          >
            {reconciling ? "Reconciling…" : "Reconcile counters"}
          </button>
        </div>
      )}
    </div>
  );
}
